/**
 * Your account, as a section of the settings page rather than a modal.
 *
 * These controls used to live in a dialog off the sidebar which mostly existed
 * to hold a link to the real settings page. Folding it in means there is one
 * answer to "where are the settings", and it is the same answer for a teacher
 * or a parent as for an administrator — they just see this section and no
 * others.
 */
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Building2, SlidersHorizontal, Users, School } from 'lucide-react';
import { Button } from '~/components/ui/button';
import SignOutButton from '~/components/SignOutButton';
import { useVersion } from '~/hooks/useVersion';
import { familyRoleIn, staffRoleIn } from '~/hooks/useRoleSelection';
import { SettingsPanelHeader, SettingsBlock } from './SettingsPanel';
import { settingsSection } from './settingsSections';
import type { SettingsOutletContext } from './SettingsLayout';

export function AccountSettings() {
  const { app, schoolName, schoolSlug } = useOutletContext<SettingsOutletContext>();
  const navigate = useNavigate();
  const { displayVersion, buildTime } = useVersion();
  const section = settingsSection('account');

  const email = app.currentUser?.email;
  const schoolCount = app.roleContext?.accessibleSchools?.length ?? 0;

  // Staff roles are not offered here: someone who runs the school and teaches a
  // class reaches the teaching views through the Teaching link, not by becoming
  // a different user first. Being a parent is the one genuinely different
  // product, and only someone who is both needs a door between them.
  const roles = app.roleContext?.accessibleSchools?.find(
    (accessible) => accessible?.school?.id === app.roleContext?.selectedSchool?.id
  )?.roles ?? [];
  const staffRole = staffRoleIn(roles);
  const familyRole = familyRoleIn(roles);
  const viewingAsFamily = app.roleContext?.selectedRole === familyRole;
  const canSwitchPortal = !!staffRole && !!familyRole;
  const builtOn = buildTime
    ? new Date(buildTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="max-w-2xl">
      <SettingsPanelHeader title={section?.label ?? 'Your account'} blurb={section?.blurb ?? ''} />

      <SettingsBlock
        title="Signed in"
        blurb={email ? `You are signed in as ${email}.` : 'You are signed in.'}
      >
        <SignOutButton destructive confirm />
      </SettingsBlock>

      <SettingsBlock
        title="School"
        blurb={
          schoolCount > 1
            ? `You are working in ${schoolName}. You have access to ${schoolCount} schools.`
            : `You are working in ${schoolName}.`
        }
      >
        {/*
          Only worth offering where there is somewhere else to go. A teacher at
          the one school they teach at would land on the picker and be bounced
          straight back, so the button is simply absent for them.
        */}
        {schoolCount > 1 && (
          <Button variant="outline" className="gap-2" onClick={() => navigate('/app')}>
            <Building2 className="h-4 w-4" />
            Change school
          </Button>
        )}
      </SettingsBlock>

      {canSwitchPortal && (
        <SettingsBlock
          title="What you're looking at"
          blurb={
            viewingAsFamily
              ? 'You are looking at your own family. You also work at this school.'
              : 'You are working at this school. You also have children enrolled here.'
          }
        >
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              app.setSelection({ role: viewingAsFamily ? staffRole! : familyRole!, schoolYearId: null });
              navigate(`/app/${schoolSlug}`);
            }}
          >
            {viewingAsFamily ? <School className="h-4 w-4" /> : <Users className="h-4 w-4" />}
            {viewingAsFamily ? 'Go to school staff' : 'Go to my family'}
          </Button>
        </SettingsBlock>
      )}

      {app.canCustomizeSidebar && (
        <SettingsBlock
          title="Sidebar"
          blurb="Reorder your links and hide the pages you never open."
        >
          <Button variant="outline" className="gap-2" onClick={app.openSidebarCustomizer}>
            <SlidersHorizontal className="h-4 w-4" />
            Customize sidebar
          </Button>
        </SettingsBlock>
      )}

      <p className="border-t pt-6 text-xs text-muted-foreground">
        SolaStack {displayVersion}
        {builtOn ? ` · built ${builtOn}` : ''}
      </p>
    </div>
  );
}

export default AccountSettings;
